import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

// Wrapper components
import { useBranding } from '../../contexts/BrandingContext';
import Footer from '../../shared-components/footer/Footer';
import Header from '../../shared-components/header/header';
import Sidebar from '../../shared-components/sidebar/Sidebar';
import ToastMessage from '../../shared-components/toast-message/ToastMessage';
// Alerts
import Alerts from '../alerts/index';
// Charging session
import ChargingSession from '../charging-session';
// Dashboard
import Dashboard from '../dashboard';
import DeviceManagement from '../device-management';
// import AddDevice from '../device-management/components/AddDevice';
import EditDevice from '../device-management/components/EditDevice';
import DynamicPricing from '../dynamic-pricing';
import FacilityTag from '../facility-tag';
// Guest charging
import GuestCharging from '../guest-charging';
import AddGuestCharging from '../guest-charging/components/AddGuestCharging';
import EditGuestCharging from '../guest-charging/components/EditGuestCharging';
// Auth pages
// import Login from '../login';
// Maintenance Scheduling
// profile
import LoginV2 from '../login-v2/components/Login';
// import RegisterV2 from '../login-v2/components/Register';
import AuthLayout from '../login-v2/index';
import ScheduleMaintenance from '../maintenance-scheduling/index';
import PenaltyManagement from '../penalty-management';
import Profile from '../profile';
// Auth pages Register
// import Register from '../register';
// Reports
import Reports from '../reports/index';
// Schedule Management
import ScheduleManagement from '../schedule-management';
// Station Management
import Settings from '../settings/Settings';
import StationManagement from '../station-management';
// import AddStation from '../station-management/components/AddStation';
import EditStation from '../station-management/components/EditStation';
import StationManagementSidePanel from '../station-management-side-panel';
// Transaction Management
import TransactionManagement from '../transaction-management';
import Forbidden from '../unauthorized_pages/forbidden';
import NetworkIssue from '../unauthorized_pages/networkIssue';
import NotFound from '../unauthorized_pages/notFound';
import PermissionDenied from '../unauthorized_pages/permissionDenied';
import RoleManagement from '../user-management/RoleManagement';
import UserManagement from '../user-management/UserManagement';
import VlbcManagement from '../vlbc-management';

import AuthGuard from './components/AuthGuard';
/**
 * Entire layout with header and sidebar
 */
function WrapperRoute() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(true);
  const { brandData } = useBranding();

  useEffect(() => {
    const isNewWindow = window.history.length <= 1;

    if (
      (location.pathname.includes('device-management/device') ||
        location.pathname.includes('station-management/approved/station') ||
        location.pathname.includes('station-management/pending/station') ||
        location.pathname.includes('station-management/rejected/station') ||
        location.pathname.includes('device-management/station') ||
        location.pathname.includes('transaction-management/station') ||
        location.pathname.includes('facility-management/station') ||
        location.pathname.includes('schedule-management/station')) &&
      isNewWindow
    ) {
      setShowSidebar(false);
    } else {
      setShowSidebar(true);
    }
  }, [location.pathname]);
  return showSidebar ? (
    <div className="ev-layout-wrap">
      <div className={`ev-sidebar ${i18n.language === 'ar' ? 'rtl' : ''}`}>
        <Sidebar />
      </div>
      <div className="ev-content-wrap">
        <div className={`ev-header ${i18n.language === 'ar' ? 'rtl' : ''}`}>
          <Header />
        </div>
        <div className="ev-content">
          <Outlet />
          {brandData?.footer && <Footer />}
        </div>
      </div>
    </div>
  ) : (
    <div className="ev-layout-wrap hide-sidebar">
      <div className="ev-content-wrap">
        <div className={`ev-header ${i18n.language === 'ar' ? 'rtl' : ''}`}>
          <Header />
        </div>
        <div className="ev-content">
          <Outlet />
          {brandData?.footer && <Footer />}
        </div>
      </div>
    </div>
  );
}

/**
 * Router component
 */
function Router() {
  const routes = [
    {
      path: 'dashboard',
      children: [
        {
          id: 1,
          path: '',
          element: <Dashboard />,
          permission: ['dashboard'],
        },
      ],
    },
    {
      path: 'station-management',
      children: [
        {
          id: 1,
          path: '',
          element: <StationManagement />,
          permission: ['charging_station'],
        },
        // {
        //   id: 2,
        //   path: 'add',
        //   element: <AddStation />,
        //   permission: ['charging_station'],
        // },
        {
          id: 3,
          path: ':page',
          element: <StationManagement />,
          permission: ['charging_station'],
        },
        {
          id: 4,
          path: 'edit/:status/:id',
          element: <EditStation />,
          permission: ['charging_station'],
        },
        {
          id: 5,
          path: 'edit/:id',
          element: <EditStation />,
          permission: ['charging_station'],
        },
        {
          id: 6,
          path: ':page/station/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'facility-management',
      children: [
        {
          id: 1,
          path: '',
          element: <FacilityTag />,
          permission: ['charging_station'],
        },
        {
          id: 2,
          path: 'station/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: ['charging_station'],
        },
      ],
    },
    {
      path: 'dynamic-pricing',
      children: [
        {
          id: 1,
          path: '',
          element: <DynamicPricing />,
          permission: ['dynamic_pricing'],
        },
      ],
    },
    {
      path: 'transaction-management',
      children: [
        {
          id: 1,
          path: '',
          element: <TransactionManagement />,
          permission: ['transaction_management'],
        },
        {
          id: 2,
          path: 'station/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'device-management',
      children: [
        {
          id: 1,
          path: '',
          element: <DeviceManagement />,
          permission: ['charging_point'],
        },
        // {
        //   id: 2,
        //   path: 'add',
        //   element: <AddDevice />,
        //   permission: ['charging_point'],
        // },
        {
          id: 4,
          path: ':page',
          element: <DeviceManagement />,
          permission: ['charging_point'],
        },
        {
          id: 2,
          path: 'edit/:id',
          element: <EditDevice />,
          permission: ['charging_point'],
        },
        {
          id: 3,
          path: 'station/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'schedule-management',
      children: [
        {
          id: 1,
          path: '',
          element: <ScheduleManagement />,
          permission: ['charging_schedule'],
        },
        {
          id: 2,
          path: ':page',
          element: <ScheduleManagement />,
          permission: ['charging_schedule'],
        },
        {
          id: 3,
          path: 'station/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'charging-session',
      children: [
        {
          id: 1,
          path: '',
          element: <ChargingSession />,
          permission: [],
        },
      ],
    },
    {
      path: 'reports',
      children: [
        {
          id: 1,
          path: ':page',
          element: <Reports />,
          permission: ['reports'],
        },
      ],
    },
    {
      path: 'guest-charging',
      children: [
        {
          id: 1,
          path: '',
          element: <GuestCharging />,
          permission: ['reports'],
        },
        {
          id: 2,
          path: 'add',
          element: <AddGuestCharging />,
          permission: ['reports'],
        },
        {
          id: 2,
          path: ':id',
          element: <EditGuestCharging />,
          permission: ['reports'],
        },
      ],
    },
    {
      path: 'user-management',
      children: [
        {
          id: 1,
          path: 'user',
          element: <UserManagement />,
          permission: ['user_management'],
        },
        {
          id: 2,
          path: 'role',
          element: <RoleManagement />,
          permission: ['user_management'],
        },
      ],
    },
    {
      path: 'maintenance-scheduling',
      children: [
        {
          id: 1,
          path: '',
          element: <ScheduleMaintenance />,
          permission: ['schedule_maintenance'],
        },
        {
          id: 3,
          path: 'station/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'profile',
      children: [
        {
          id: 1,
          path: '',
          element: <Profile />,
          permission: ['profile'],
        },
      ],
    },
    {
      path: 'alerts',
      children: [
        {
          id: 1,
          path: ':page',
          element: <Alerts />,
          permission: ['alerts'],
        },
      ],
    },
    {
      path: 'no-data',
      children: [
        {
          id: 1,
          path: '',
          element: <Forbidden />,
          permission: [],
        },
      ],
    },
    {
      path: 'network-issues',
      children: [
        {
          id: 1,
          path: '',
          element: <NetworkIssue />,
          permission: [],
        },
      ],
    },
    {
      path: 'permission-denied',
      children: [
        {
          id: 1,
          path: '',
          element: <PermissionDenied />,
          permission: [],
        },
      ],
    },
    {
      path: 'vlbc',
      children: [
        {
          id: 1,
          path: '',
          element: <VlbcManagement />,
          permission: ['reports'],
        },
      ],
    },
    {
      path: 'settings',
      children: [
        {
          id: 1,
          path: '',
          element: <Settings />,
          permission: [],
        },
      ],
    },
    {
      path: 'penalty-management/:currentPage',
      children: [
        {
          id: 1,
          path: '',
          element: <PenaltyManagement />,
          permission: [],
        },
      ],
    },
  ];
  return (
    <>
      <Routes>
        {/* <Route path="/login/:page" element={<Login />} /> */}
        {/* <Route path="/login/:page/:subpage" element={<Login />} /> */}
        {/* <Route path="/" element={<Navigate to="/login/credentials" />} /> */}
        {/* <Route path="/register" element={<Register />} /> */}
        <Route path="/" element={<Navigate to="/login/credentials" />} />
        <Route path="/login" element={<Navigate to="/login/credentials" replace />} />
        <Route element={<AuthLayout />}>
          <Route path="login/:page" element={<LoginV2 />} />
          <Route path="login/:page/:subpage" element={<LoginV2 />} />
        </Route>
        <Route path="/ev-admin" element={<WrapperRoute />}>
          {routes.map((route) => (
            <Route path={route.path}>
              {route.children.map((subRoute) => (
                <Route
                  key={subRoute?.id}
                  path={subRoute?.path}
                  element={
                    <AuthGuard permission={subRoute?.permission} Component={subRoute?.element} />
                  }
                />
              ))}
            </Route>
          ))}
        </Route>
        <Route path="/*" element={<NotFound />} />
      </Routes>
      <ToastMessage />
    </>
  );
}

export default Router;
